import _ from 'lodash';

import {
  Environment,
  Access,
  Role,
  EnvironmentType,
} from '@/react/portainer/environments/types';
import { EnvironmentGroup } from '@/react/portainer/environments/environment-groups/types';
import { useTeams } from '@/react/portainer/users/teams/queries';
import {
  UserAccessViewModel,
  TeamAccessViewModel,
} from '@/portainer/models/access';
import { useRbacRoles } from '@/react/portainer/users/RolesView/useRbacRoles';
import { isVersionSmallerOrEqual } from '@/react/common/semver-utils';
import { useUsers } from '@/portainer/users/queries/useUsers';

import { PolicyType, getMinimumAgentVersion } from '../../policies/types';

type Accesses = {
  availableUsersAndTeams: Access[];
  authorizedUsersAndTeams: Access[];
  roles: Role[];
  policyID?: number;
  policyName?: string;
  hasPolicyOverride: boolean;
  isLoading: boolean;
  error: Error | null;
};

const emptyAccesses: Accesses = {
  availableUsersAndTeams: [],
  authorizedUsersAndTeams: [],
  roles: [],
  hasPolicyOverride: false,
  isLoading: false,
  error: null,
};

export function useAccesses(
  environment?: Environment,
  group?: EnvironmentGroup,
  minimumAgentVersions?: Record<PolicyType, string>,
  // callers that only need the policy-override fields (e.g. a read-only
  // summary) should pass false, so the underlying full users/teams/roles
  // directory is never fetched for non-admin viewers
  includeAccessData = true
): Accesses {
  const queriesEnabled = includeAccessData && (!!environment || !!group);
  const rolesQuery = useRbacRoles({
    enabled: queriesEnabled,
    select: (roles) => _.orderBy(roles, 'Priority', 'asc') as Role[],
  });
  const usersAccessesQuery = useUsers(
    false,
    undefined,
    queriesEnabled,
    (users) => users.map((user) => new UserAccessViewModel(user))
  );
  const teamsAccessesQuery = useTeams(false, undefined, {
    enabled: queriesEnabled,
    select: (teams) => teams.map((team) => new TeamAccessViewModel(team)),
  });
  const roles = rolesQuery.data;
  const usersAccesses = usersAccessesQuery.data;
  const teamsAccesses = teamsAccessesQuery.data;
  const isLoading =
    queriesEnabled &&
    (usersAccessesQuery.isLoading ||
      teamsAccessesQuery.isLoading ||
      rolesQuery.isLoading);
  const error = queriesEnabled
    ? (usersAccessesQuery.error as Error | null) ||
      (teamsAccessesQuery.error as Error | null) ||
      (rolesQuery.error as Error | null)
    : null;

  const policyData = getRBACPolicy(environment, minimumAgentVersions);

  if (!environment && !group) {
    return emptyAccesses;
  }

  if (!includeAccessData) {
    return {
      ...emptyAccesses,
      policyID: policyData ? policyData.policyID : 0,
      policyName: policyData ? policyData.policyName : '',
      hasPolicyOverride: !!policyData,
    };
  }

  if (!usersAccesses || !teamsAccesses || !roles) {
    return emptyAccesses;
  }

  const authorizedUserPolicies = environment?.UserAccessPolicies || {};
  const authorizedTeamPolicies = environment?.TeamAccessPolicies || {};
  let inheritedUserPolicies = group?.UserAccessPolicies || {};
  let inheritedTeamPolicies = group?.TeamAccessPolicies || {};

  // when the environment has a policy override:
  // - any group values should be ignored and not shown
  // - the environment values represent the policy values (no change needed)
  if (policyData) {
    inheritedUserPolicies = {};
    inheritedTeamPolicies = {};
  }

  const userAccessData = mapAccessData(
    usersAccesses,
    authorizedUserPolicies,
    inheritedUserPolicies,
    roles,
    policyData ? policyData.policyID : 0
  );
  const teamAccessData = mapAccessData(
    teamsAccesses,
    authorizedTeamPolicies,
    inheritedTeamPolicies,
    roles,
    policyData ? policyData.policyID : 0
  );

  return {
    availableUsersAndTeams: userAccessData.available.concat(
      teamAccessData.available
    ),
    authorizedUsersAndTeams: userAccessData.authorized.concat(
      teamAccessData.authorized
    ),
    roles,
    policyID: policyData ? policyData.policyID : 0,
    policyName: policyData ? policyData.policyName : '',
    hasPolicyOverride: !!policyData,
    isLoading,
    error,
  };
}

/**
 * get the RBAC policy for the environment
 * returns the policy ID and name if the environment supports RBAC policies
 * returns false if the environment does not support RBAC policies
 */
function getRBACPolicy(
  environment?: Environment,
  minimumAgentVersions?: Record<PolicyType, string>
):
  | {
      policyID: number;
      policyName: string;
    }
  | false {
  if (!environment) {
    return false;
  }
  // does the environment type support RBAC policies
  const isKubeEdge = environment.Type === EnvironmentType.EdgeAgentOnKubernetes;
  const isDocker =
    environment.Type === EnvironmentType.Docker ||
    environment.Type === EnvironmentType.AgentOnDocker ||
    environment.Type === EnvironmentType.EdgeAgentOnDocker;
  if (!isKubeEdge && !isDocker) {
    return false;
  }

  // does the agent version support RBAC policies
  const rbacPolicyType: PolicyType = isKubeEdge ? 'rbac-k8s' : 'rbac-docker';
  const minimumVersionStr = getMinimumAgentVersion(
    rbacPolicyType,
    minimumAgentVersions
  );
  if (minimumVersionStr) {
    const agentVersionStr = environment.Agent?.Version || '0.0.0';
    const agentVersionSupported = isVersionSmallerOrEqual(
      minimumVersionStr,
      agentVersionStr
    );
    if (!agentVersionSupported) {
      return false;
    }
  }

  const policyID = isKubeEdge
    ? environment.Policies?.k8sRBACPolicy?.policyID
    : environment.Policies?.dockerRBACPolicy?.policyID;
  const policyName = isKubeEdge
    ? environment.Policies?.k8sRBACPolicy?.policyName
    : environment.Policies?.dockerRBACPolicy?.policyName;
  if (!policyID || !policyName) {
    return false;
  }

  return {
    policyID,
    policyName,
  };
}

function getRole(roles: Role[], roleId: number): Role {
  if (roles.length) {
    const role = roles.find((r) => r.Id === roleId);
    return role || { Id: 0, Name: '-', Priority: 0 };
  }
  return { Id: 0, Name: '-', Priority: 0 };
}

function mapAccessData(
  accesses: Access[],
  authorizedPolicies: Record<number, { RoleId: number }>,
  inheritedPolicies: Record<number, { RoleId: number }>,
  roles: Role[],
  policyID: number = 0
) {
  const availableAccesses: Access[] = [];
  const authorizedAccesses: Access[] = [];

  accesses.forEach((access) => {
    const authorized = authorizedPolicies[access.Id];
    const inherited = inheritedPolicies[access.Id];

    if (authorized && inherited) {
      const accessWithOverride = {
        ...access,
        Role: getRole(roles, authorized.RoleId),
        Override: policyID === 0,
      };
      authorizedAccesses.push(accessWithOverride);
      if (policyID !== 0) {
        const accessPolicyInherited = {
          ...access,
          Role: getRole(roles, inherited.RoleId),
          Override: false,
          Inherited: true,
          PolicyID: policyID,
        };
        authorizedAccesses.push(accessPolicyInherited);
      }
    } else if (authorized && !inherited) {
      const accessWithRole = {
        ...access,
        Role: getRole(roles, authorized.RoleId),
      };
      authorizedAccesses.push(accessWithRole);
    } else if (!authorized && inherited) {
      const inheritedAccess = {
        ...access,
        Role: getRole(roles, inherited.RoleId),
        Inherited: true,
        PolicyID: policyID,
      };
      authorizedAccesses.push(inheritedAccess);
      availableAccesses.push(inheritedAccess);
    } else {
      availableAccesses.push(access);
    }
  });

  return {
    available: availableAccesses,
    authorized: authorizedAccesses,
  };
}
